/**
 * Правая колонка — консоль. ЧЕТЫРЕ РЕГИОНА и один скролл:
 *
 *   ШАПКА ПОЛЯ      — имя, площадь, бейдж типа, полоса вкладок. Не прокручивается.
 *   ПАНЕЛЬ ВКЛАДКИ  — единственный скролл: «Анализ» или «Заметки», смонтирована ровно одна.
 *   ПАНЕЛЬ ДЕЙСТВИЙ — закреплена внизу, высота НИКОГДА не меняется: [Анализ поля].
 *   Слежение из неё убрано 2026-10-02; высота полосы прежняя — она про смещение органа от
 *   собственного нажатия, а не про число кнопок.
 *   ПОДВАЛ          — `DevPanel`, висит на `devMode`, общий и от вкладки не зависящий.
 *
 * ВКЛАДКИ, А НЕ БЛОК (notes-v2). «Что говорит спутник» читают дома, «что я видел на месте»
 * пишут в поле, и второй вопрос стоял ПОД первым: чтобы дописать заметку, надо было прокрутить
 * мимо ответа, доказательства, снимков и истории. Заодно исчезли два правила, которые
 * существовали ровно потому, что контейнер был мал, — «видны первые пять карточек» и «раскрыта
 * одна за раз». Правило «в теле консоли ровно один скролл» при этом осталось истинным
 * дословно: смонтирована всегда одна панель.
 *
 * НЕАКТИВНАЯ ПАНЕЛЬ РАЗМОНТИРУЕТСЯ, А НЕ ПРЯЧЕТСЯ СТИЛЕМ: спрятанный список из двадцати
 * заметок держал бы шестьдесят blob-URL всё время, пока человек читает анализ. Поэтому всё
 * изменчивое состояние вкладки поднято НАД ней, в `useNotesUi` (владелец — `MapScreen`), и
 * переключение не теряет ни черновика, ни открытого редактора, ни раскрытых карточек.
 *
 * ОДИН ВЛАДЕЛЕЦ НА ТЕМУ (§3.1, §11.2). До 2026-09-04 о состоянии поля высказывались четверо
 * сразу — заголовок типа поля, заголовок вегетации и карточка ответа, — и
 * арбитра между ними не было. Теперь состояние называет карточка и только она; количество
 * снимков считает блок ③ и только он; идущий прогон произносит кнопка и только она.
 *
 * SEALING IS NOT PRODUCT BEHAVIOUR — it lives behind `verificationMode` and nothing else.
 * The rule (a finished run's verdict is hidden until an observation is recorded) exists so
 * that OUR agreement number measures the model instead of our own suggestion. Inside that
 * mode blocks ① and ② are absent from the DOM; the type badge and the action panel stay,
 * because neither can hint whether the field was harvested this week (§6.8).
 */

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import "./Console.css";

import { T } from "../strings";
import type {
  Field,
  FieldUse as FieldUseData,
  GroundTruth,
  GroundTruthInput,
  Run,
} from "../api/types";
import {
  formatDayMonth,
  formatDayMonthTime,
  formatFull,
  formatFullTime,
  hectares,
} from "../lib/format";
import { buildStrip } from "../lib/strip";
import {
  fieldUseFamily,
  noDataHeading,
  verdictCaveat,
  verdictFromRun,
  verdictHeading,
  verdictKindOfDecision,
} from "../lib/verdict";
import { dynamicsLine, surfaceZoneOf, vegetationFromRun } from "../lib/vegetation";
import { useAdvanced } from "../hooks/useAdvanced";
import { useDevMode } from "../hooks/useDevMode";
import type { FieldUseState, QueueEntry, RunState } from "../state/useWorkspace";
import type { RunFlags } from "../api/types";
import { ConsoleTabs, TAB_IDS, type ConsoleTab } from "./ConsoleTabs";
import { DevPanel } from "./DevPanel";
import { FieldNotes, type NotesPanel } from "./FieldNotes";
import { GroundTruthForm } from "./GroundTruthForm";
import { RejectedLog } from "./RejectedLog";
import { currentStepLabel, useElapsedSeconds } from "./RunProgress";
import { StateStrip } from "./StateStrip";
import { Vegetation } from "./Vegetation";
import { VerdictBlock } from "./Verdict";
import { POLICY_RADAR_UNRELIABLE_AREA_HA, POLICY_SMALL_FIELD_AREA_HA } from "../lib/constants";

/** Ребро между блоками тела. Заголовок рисует ребёнок — он и есть ответ блока. */
const Block = ({ testId, children }: { testId: string; children: ReactNode }) => (
  <section className="k-block" data-testid={testId}>
    {children}
  </section>
);

interface Props {
  field: Field | null;
  fieldsCount: number;
  fieldsLoading: boolean;
  runs: Run[];
  activeRun: Run | null;
  onSelectRun: (runId: number) => void;
  runState: RunState;
  onRun: (flags?: RunFlags) => void;
  onRunBatch: () => void;
  checkedCount: number;
  queue: QueueEntry[];
  onClearQueue: () => void;
  onSelectField: (fieldId: number) => void;
  flags: RunFlags;
  onFlagsChange: (flags: RunFlags) => void;
  analysisDate: string;
  onAnalysisDate: (date: string) => void;
  groundTruth: GroundTruth[];
  onAddGroundTruth: (input: GroundTruthInput) => Promise<boolean>;
  /** Тип поля: null — не считалось, undefined — ещё не спрашивали у сервера. */
  fieldUse: FieldUseData | null | undefined;
  fieldUseState: FieldUseState | undefined;
  onComputeFieldUse: () => void;
  /** Серверный флаг проверочной поверхности. Печать бывает только внутри него. */
  verificationMode: boolean;
  /**
   * True while the verdict of `activeRun` must not be shown. Owned by the workspace, and
   * it can only be true in verification mode — see the module docstring.
   */
  verdictSealed: boolean;
  onReveal: (runId: number) => void;
  blindOpen: boolean;
  onBlindOpen: (open: boolean) => void;
  revealToken: number;
  animate: boolean;
  onPickDate: (date: string) => void;
  /** Дата, которую показывает превью: полоса состояния помечает её и читает вслух. */
  previewDate: string | null;
  actionError: string | null;
  /**
   * Всё про заметки поля одним пропом, а не тремя десятками. Панель вкладки — самостоятельный
   * орган со своей загрузкой, своей очередью и своими отказами; консоль его только ставит на
   * место и не читает из него ни одного значения, кроме счёта для ярлыка вкладки. `null` —
   * заметок не показываем вовсе (поля нет, печать).
   */
  notes: NotesPanel | null;
  /**
   * «Заметки N» из карточки поля в ростере. Растёт на каждое нажатие — и только на нажатие:
   * без счётчика второе нажатие подряд неотличимо от первого ни одним полем, а вкладка уже
   * стоит там, куда её просят поставить.
   */
  notesTabToken: number;
}

export function Console({
  field,
  fieldsCount,
  fieldsLoading,
  runs,
  activeRun,
  onSelectRun,
  runState,
  onRun,
  onRunBatch,
  checkedCount,
  queue,
  onClearQueue,
  onSelectField,
  flags,
  onFlagsChange,
  analysisDate,
  onAnalysisDate,
  groundTruth,
  onAddGroundTruth,
  fieldUse,
  fieldUseState,
  onComputeFieldUse,
  verificationMode,
  verdictSealed,
  onReveal,
  blindOpen,
  onBlindOpen,
  revealToken,
  animate,
  onPickDate,
  previewDate,
  actionError,
  notes,
  notesTabToken,
}: Props) {
  const { devMode, toggleDev } = useDevMode();
  const { advanced } = useAdvanced();
  // Связь односторонняя: служебный режим показывает числа, «Числа» служебную панель не
  // открывают — это две разные популяции и два разных вопроса к экрану.
  const showNumbers = advanced || devMode;
  // Раскрытие графика на сессию: кто открыл его один раз, не открывает заново на каждом поле.
  const [chartOpen, setChartOpen] = useState(false);
  /**
   * ВЫБОР ВКЛАДКИ — СВОЙСТВО СЕССИИ, А НЕ ПОЛЯ, тот же закон, что у `chartOpen`: человек,
   * читающий заметки по трём полям подряд, не должен возвращаться на «Заметки» после каждого
   * клика в ростере. По умолчанию «Анализ» — с него начинают знакомство с полем.
   */
  const [tab, setTab] = useState<ConsoleTab>("analysis");
  const busy = runState.phase === "waiting" || runState.phase === "running";
  const elapsed = useElapsedSeconds(runState);
  const step = currentStepLabel(runState);

  /**
   * «Заметки N» в карточке поля ростера ставит вкладку — и это ОДИН из двух явных жестов,
   * которым это позволено (второй — клик по самому ярлыку).
   */
  const seenToken = useRef(notesTabToken);
  useEffect(() => {
    if (notesTabToken === seenToken.current) return;
    seenToken.current = notesTabToken;
    setTab("notes");
  }, [notesTabToken]);

  /**
   * ЕДИНСТВЕННОЕ САМОВОЛЬНОЕ ПЕРЕКЛЮЧЕНИЕ, и условие у него узкое: прогон, запущенный В ЭТОЙ
   * СЕССИИ И ВРУЧНУЮ, перешёл в `done` или `error` — ответ пришёл, и он на другой вкладке.
   *
   * Пакетный прогон этого права не имеет (`source: "batch"`): он заканчивается двадцать раз
   * подряд, переключая выбранное поле на каждом шаге, и вкладка дёргалась бы у того, кто в это
   * время читает заметки. Несохранённое при переключении не гибнет — состояние живёт над
   * вкладкой, — но отнимать экран у человека без спроса всё равно нельзя дважды.
   */
  const seenPhase = useRef(runState.phase);
  useEffect(() => {
    const was = seenPhase.current;
    seenPhase.current = runState.phase;
    if (was === runState.phase) return;
    if (runState.source !== "single") return;
    if (runState.phase === "done" || runState.phase === "error") setTab("analysis");
  }, [runState.phase, runState.source]);

  const useCode = fieldUse?.field_use_code ?? null;
  const family = fieldUseFamily(useCode);
  const verdict = verdictFromRun(activeRun, useCode);
  // Зона берётся С ВЕРДИКТА, а не считается заново: заголовок, тон и оговорка обязаны стоять
  // на одном и том же снимке, иначе «пахота» окажется под оговоркой про стерню.
  const caveat = verdict === null ? null : verdictCaveat(verdict.kind, useCode, verdict.zone);
  /**
   * ХОД РАСТИТЕЛЬНОСТИ — строка НАД карточкой, и молчит она в шести случаях. Четыре считает
   * `dynamicsLine` (тренда нет, даты снимка нет, прогона/состояния нет, снимки старые), два
   * держатся здесь и только здесь, потому что это состояния КОЛОНКИ, а не наблюдения:
   * вердикта нет вовсе (S2 «поле не анализировано», S3, упавшая попытка) и вердикт говорит
   * «Нет чётких снимков» — там о ходе сказать нечего тем же прогоном, который не смог сказать
   * о поле. Шестой — печать: в запечатанном режиме ветка с карточкой не рендерится целиком.
   * В S5 (идёт следующий анализ) строка ОСТАЁТСЯ: она принадлежит показанному прогону, как и
   * карточка под ней.
   */
  const dynamics =
    verdict === null || verdict.kind === "nodata"
      ? null
      : dynamicsLine(vegetationFromRun(activeRun), verdict.zone);
  const strip = buildStrip(activeRun);

  // The server's own size verdict; the constants are only a fallback for an older response.
  const smallThreshold = field?.size?.small_field_threshold_ha ?? POLICY_SMALL_FIELD_AREA_HA;
  const radarThreshold = field?.size?.radar_threshold_ha ?? POLICY_RADAR_UNRELIABLE_AREA_HA;
  const isSmall = field ? (field.size?.small_field ?? field.area_ha < smallThreshold) : false;
  const radarUnreliable = field
    ? (field.size?.radar_unreliable ?? field.area_ha < radarThreshold)
    : false;

  // --- семь состояний колонки (§5) ---------------------------------------
  // ДВА РАЗНЫХ ОТКАЗА, и склеивать их нельзя. «Попытка упала» — про эту сессию; «показанный
  // прогон — отказ» — про то, что сейчас на экране. При отказе СЕТИ на повторном анализе
  // `activeRunId` остаётся на прежнем удачном прогоне, и один общий флаг убирал доказательство
  // и снимки из-под ответа, который никто не стирал.
  const attemptFailed = runState.phase === "error";
  // Показанный прогон сохранён упавшим. Заголовок отказа принадлежит карточке и печатается
  // один раз; он же выбирает, есть ли что доказывать блоками ② и ③.
  const shownFailed = verdict?.failed === true;
  // Кнопка говорит о ПОПЫТКЕ: повторяют не показанный прогон, а тот, что не удался.
  const failed = attemptFailed || shownFailed;
  // S2: поле выбрано, а прогонов по нему нет вовсе — ни показанного, ни идущего. Счёт с
  // сервера обязателен: список прогонов поля грузится отдельной просьбой и до ответа выглядит
  // пустым, а «Поле ещё не анализировано» на поле с ответами — мигалка, а не состояние.
  const neverRun =
    field !== null &&
    (field.run_count ?? 0) === 0 &&
    runs.length === 0 &&
    activeRun === null &&
    !busy &&
    !attemptFailed;
  // S5: прежний ответ остаётся на экране, пока считается следующий.
  const nextRunPending = busy && verdict !== null;

  /**
   * Служебная панель — ручка прибора, и висит она на `devMode`, а не на «числах» (§4.2):
   * так подвал перестал прыгать на 84 px при включении чисел, а сырые флаги перестали
   * показываться копателю. `Ctrl+Alt+D` остаётся аварийным входом.
   */
  const devPanel = devMode && (
    <DevPanel
      open={devMode}
      onToggle={toggleDev}
      field={field}
      activeRun={activeRun}
      runState={runState}
      verdict={verdict}
      strip={strip}
      busy={busy}
      flags={flags}
      onFlagsChange={onFlagsChange}
      analysisDate={analysisDate}
      onAnalysisDate={onAnalysisDate}
      checkedCount={checkedCount}
      onRunBatch={onRunBatch}
      queue={queue}
      onClearQueue={onClearQueue}
      onSelectField={onSelectField}
      fieldUse={fieldUse ?? null}
      verificationMode={verificationMode}
      sealed={verdictSealed}
      groundTruthCount={groundTruth.length}
      onOpenObservation={() => onBlindOpen(true)}
    />
  );

  // --- S0: полей нет вовсе -----------------------------------------------
  if (fieldsLoading && fieldsCount === 0) {
    return (
      <aside className="k-console">
        <div className="k-console__body k-scroll">
          <p className="k-caption">{T.common.loading}</p>
        </div>
        {devPanel}
      </aside>
    );
  }

  if (fieldsCount === 0) {
    return (
      <aside className="k-console">
        <div className="k-console__body k-scroll">
          <div className="k-empty">
            <span className="k-empty__title">{T.firstRun.title}</span>
            <p className="k-hint">{T.firstRun.step}</p>
          </div>
        </div>
        {devPanel}
      </aside>
    );
  }

  // --- S1: поле не выбрано. Панель действий СКРЫТА: нажимать нечего ------
  if (field === null) {
    return (
      <aside className="k-console">
        <header className="k-console__head">
          <h2 className="k-console__name">{T.blocks.noField}</h2>
        </header>
        <div className="k-console__body k-scroll">
          <p className="k-caption">{T.blocks.noFieldInvite}</p>
        </div>
        {devPanel}
      </aside>
    );
  }

  /** Ворота печати. Существуют только в режиме верификации — см. docstring модуля. */
  const sealedGate = (
    <div className="k-section k-section--tight">
      <div className="k-section__head">
        <span className="k-label">{T.groundTruth.title}</span>
        {groundTruth.length > 0 && (
          <span className="k-hint">{T.groundTruth.existing(groundTruth.length)}</span>
        )}
      </div>
      {/* Нейтральная, а не охряная: заливка охрой принадлежит одному действию экрана. */}
      <button type="button" className="k-btn k-btn--block" onClick={() => onBlindOpen(true)}>
        {T.groundTruth.open}
      </button>
    </div>
  );

  const observationFormOpen = blindOpen;
  const observationForm = observationFormOpen && (
    <div className="k-section">
      <GroundTruthForm
        fieldName={field.name}
        verdict={verdict}
        existingCount={groundTruth.length}
        runId={activeRun?.id ?? null}
        blind={verdictSealed}
        onSubmit={onAddGroundTruth}
        onClose={() => onBlindOpen(false)}
      />
    </div>
  );

  /**
   * Ярлык прогона для списка и для строки-заглядывания — тот же заголовок, что и на карточке:
   * сырой enum в канале вердикта был бы ровно той протечкой, ради которой правило заголовков и
   * написано. Зона берётся у САМОГО прогона строки, а не у показанного: у прошлого анализа своя
   * поверхность, и «уборка» в истории могла стать «обработкой почвы» только на нём.
   */
  const runLabel = (run: Run): string => {
    if (!run.result?.decision) return T.run.failed;
    const kind = verdictKindOfDecision(run.result.decision);
    if (kind === "nodata") return noDataHeading(run);
    return verdictHeading(kind, family, surfaceZoneOf(run));
  };

  /**
   * Дата прогона в списке. Два прогона одного дня различает только час, и тогда — и только
   * тогда — к дате добавляется время старта (§6.5); два прогона одной МИНУТЫ различают
   * только секунды — тогда печатаются и они (run-time-seconds).
   */
  const sameDayRuns = new Set(
    runs.map((run) => run.analysis_date).filter((date, index, all) => all.indexOf(date) !== index),
  );
  // Ключ минуты — по сырой ISO-строке (у всех строк один формат); печать остаётся локальной.
  const minuteKey = (run: Run): string =>
    `${run.analysis_date}|${(run.started_at ?? "").slice(0, 16)}`;
  const sameMinuteRuns = new Set(
    runs.map(minuteKey).filter((key, index, all) => all.indexOf(key) !== index),
  );
  const runDate = (run: Run): string =>
    sameDayRuns.has(run.analysis_date)
      ? formatDayMonthTime(run.analysis_date, run.started_at, sameMinuteRuns.has(minuteKey(run)))
      : formatFull(run.analysis_date);

  // Анализ ПЕРЕД показанным: список приходит от свежего к старому.
  const shownIndex = runs.findIndex((run) => run.id === activeRun?.id);
  const previousRun = runs[(shownIndex < 0 ? 0 : shownIndex) + 1] ?? null;

  // --- панель действий: ОДНА кнопка, и её текст — состояние прогона ------
  const runLabelText = busy
    ? T.run.busy(step, elapsed)
    : failed
      ? T.run.retry
      : neverRun
        ? T.run.start
        : T.run.startAgain;
  // Охра — только там, где ответа ещё нет: первое знакомство с полем и повтор после отказа.
  const runIsGoal = !busy && (neverRun || failed);

  const badgeLabel = useCode === null ? null : T.fieldUse.codes[useCode];

  /**
   * Блок ⑤: чем посчитан ответ. Только «при числах» (§6.7) и только на поле, где анализ
   * был (provenance-s2): на поле без единого прогона блоку нечего подписывать.
   */
  const provenance = showNumbers && !neverRun && (
    <Block testId="block-provenance">
      <h3 className="k-block__title">{T.advanced.blockTitle}</h3>

      {fieldUse && (
        <p className="k-hint k-mono" data-testid="provenance-use">
          {T.fieldUse.computedAt(formatFullTime(fieldUse.computed_at))} ·{" "}
          {T.fieldUse.window(fieldUse.history_years, formatFull(fieldUse.analysis_date))}
        </p>
      )}

      {activeRun && (
        <dl className="k-readout" data-testid="provenance-run">
          <dt>{T.run.analysisDate}</dt>
          <dd>{formatFull(activeRun.analysis_date)}</dd>
        </dl>
      )}

      {fieldUseState?.phase === "running" && (
        <p className="k-caption" data-testid="field-use-running">
          {T.fieldUse.running}
        </p>
      )}
      {fieldUseState?.error != null && (
        <p className="k-note k-note--error">{T.fieldUse.failed(fieldUseState.error)}</p>
      )}
      {fieldUse === null && (
        <p className="k-hint" data-testid="field-use-none">
          {T.fieldUse.notComputed}. {T.fieldUse.notComputedHint}
        </p>
      )}

      {/* Одна кнопка, подпись по факту (fielduse-hint): «Пересчитать» над непосчитанным
          типом обещало расчёт, которого не было, а хинт выше зовёт кнопку по имени. */}
      <button
        type="button"
        className="k-btn k-btn--ghost k-btn--block"
        disabled={fieldUseState?.phase === "running"}
        onClick={onComputeFieldUse}
      >
        {fieldUse === null ? T.fieldUse.compute : T.fieldUse.recompute}
      </button>
    </Block>
  );

  /**
   * ПОЛОСЫ ВКЛАДОК НЕТ ВОВСЕ В ЧЕТЫРЁХ СОСТОЯНИЯХ: полей нет (S0), поле не выбрано (S1) —
   * обе ветки вышли выше, — запечатанный режим и форма наблюдения поверх него. Одна вкладка —
   * это не вкладки, и в этих состояниях консоль выглядит ровно как до notes-v2.
   */
  const sealedSurface = verdictSealed || observationFormOpen;
  const tabsVisible = notes !== null && !sealedSurface;
  const showNotes = tabsVisible && tab === "notes";

  /** Отказ действия — нотой НАД панелью, внутри активной панели вкладки. */
  const errorNote = actionError && (
    <div className="k-note k-note--error" data-testid="action-error">
      {actionError}
    </div>
  );

  const analysisPanel = (
    <>
      {/* S5: прежний ответ остаётся, и полоса говорит, ПОЧЕМУ он старый. */}
      {nextRunPending && (
        <div className="k-note k-note--info" data-testid="new-run-banner">
          {T.blocks.newRunBanner}
        </div>
      )}

      {verdictSealed && activeRun && !observationFormOpen ? (
        /* Verification mode only: blocks ① and ② are absent from the DOM, not hidden by
           CSS. `verdictSealed` cannot be true outside that mode. */
        <section className="k-section k-blind" data-testid="verdict-sealed">
          <div className="k-blind__banner">{T.groundTruth.sealed}</div>
          <p className="k-hint">{T.groundTruth.sealedHint}</p>
          {sealedGate}
          <button
            type="button"
            className="k-btn k-btn--ghost k-btn--block"
            onClick={() => onReveal(activeRun.id)}
          >
            {T.groundTruth.reveal}
          </button>
          <p className="k-hint">{T.groundTruth.revealHint}</p>
        </section>
      ) : verdictSealed && observationFormOpen ? (
        observationForm
      ) : (
        <>
          {/* ХОД РАСТИТЕЛЬНОСТИ — единственная строка экрана ВЫШЕ заголовка ответа. Это
              контекст, а не второй вердикт. */}
          {dynamics && (
            <p className="k-caption k-console__dynamics" data-testid="vegetation-dynamics">
              {dynamics}
            </p>
          )}

          {/* ① КАРТОЧКА ОТВЕТА — первая, над сгибом. */}
          {neverRun ? (
            <Block testId="block-answer">
              <h2 className="k-block__title" data-testid="no-run">
                {T.blocks.noRun}
              </h2>
              <p className="k-caption">{T.blocks.noRunInvite}</p>
            </Block>
          ) : verdict ? (
            <Block testId="block-answer">
              <VerdictBlock
                verdict={verdict}
                revealKey={revealToken}
                caveat={caveat}
                showNumbers={showNumbers}
                previewDate={strip?.latestAcceptedDate ?? verdict.latestClearDate}
                onShowDate={onPickDate}
              />
            </Block>
          ) : attemptFailed ? (
            <Block testId="block-answer">
              <h2 className="k-block__title k-verdict__state" data-testid="verdict-state">
                {T.run.failed}
              </h2>
              {/* Причина — по КОДУ и по-русски (run-error-priority). */}
              <p className="k-verdict__human" data-testid="verdict-reason">
                {activeRun?.error_reason
                  ? T.reason.byCode(activeRun.error_reason)
                  : T.reason.unknown}
              </p>
            </Block>
          ) : null}

          {/* ② ДОКАЗАТЕЛЬСТВО — без своего заголовка. */}
          {!neverRun && !shownFailed && activeRun && (
            <Block testId="block-veg">
              <Vegetation
                run={activeRun}
                revealKey={revealToken}
                devMode={devMode}
                showNumbers={showNumbers}
              />
            </Block>
          )}

          {/* ③ СНИМКИ — ОДИН знаменатель на экран, одной строкой-summary (§3.5, §11.5). */}
          {!neverRun && !shownFailed && strip && (
            <Block testId="block-strip">
              <details
                className="k-details k-details--major"
                open={chartOpen}
                onToggle={(event) => setChartOpen(event.currentTarget.open)}
                data-testid="strip-disclosure"
              >
                <summary>
                  {T.strip.summary(
                    strip.total,
                    strip.acceptedCount,
                    strip.latestAcceptedDate === null
                      ? null
                      : formatDayMonth(strip.latestAcceptedDate),
                  )}
                </summary>
                {/* СОДЕРЖИМОЕ МОНТИРУЕТСЯ ТОЛЬКО РАСКРЫТЫМ: «спрятан» и «не нарисован» —
                    разные вещи, и закрытый график всё равно держал `ResizeObserver`. */}
                {chartOpen && (
                  <>
                    <p className="k-strip__reading" data-testid="strip-reading">
                      {T.strip.reading}
                    </p>
                    <StateStrip
                      model={strip}
                      revealKey={revealToken}
                      animate={animate}
                      onPickDate={onPickDate}
                      selectedDate={previewDate}
                      dev={devMode}
                    />
                    {showNumbers && <RejectedLog model={strip} />}
                  </>
                )}
              </details>
            </Block>
          )}

          {/* Форма ВМЕСТО кнопки, а не вместо ответа. */}
          {observationFormOpen ? observationForm : verificationMode && sealedGate}

          {/* ④ ПРОШЛЫЕ АНАЛИЗЫ. */}
          {runs.length > 1 && (
            <Block testId="block-history">
              <details className="k-details k-details--major" data-testid="run-history">
                <summary>
                  <span>{T.run.history}</span>
                  <span className="k-details__key">{runs.length}</span>
                </summary>
                <div className="k-log">
                  {runs.map((run) => (
                    <button
                      key={run.id}
                      type="button"
                      className="k-runrow"
                      aria-current={run.id === activeRun?.id}
                      onClick={() => onSelectRun(run.id)}
                    >
                      <span className="k-mono">{runDate(run)}</span>
                      <span className="k-hint">{runLabel(run)}</span>
                    </button>
                  ))}
                </div>
              </details>
              {previousRun && (
                <p className="k-details__peek k-hint" data-testid="history-peek">
                  {T.run.historyPeek(runDate(previousRun), runLabel(previousRun))}
                </p>
              )}
            </Block>
          )}

          {/* БЛОКА ФОТОГРАФИЙ ЗДЕСЬ БОЛЬШЕ НЕТ (notes-v2): фотография живёт внутри заметки, а
              заметки — на своей вкладке. Анализ снова отвечает ровно на один вопрос. */}

          {provenance}
        </>
      )}

      {errorNote}
    </>
  );

  return (
    <aside
      className="k-console"
      /* ГЛУШИТЕЛЬ ПРОМАХА. Зона дропа фотографий — панель «Заметки», и файл, брошенный мимо
         неё, по умолчанию уводит БРАУЗЕР на этот файл: приложения больше нет, а вместе с ним
         нет и несохранённого контура. Отмена на корне колонки стоит две строки и закрывает
         весь промах разом; сам файл здесь не принимается — промах должен кончаться ничем. */
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => event.preventDefault()}
    >
      {/* ШАПКА. Имя одной строкой с усечением и полным именем в `title`; площадь моно справа
          на той же базовой линии; бейдж типа — второй ряд; полоса вкладок — последний. */}
      <header className="k-console__head">
        <div className="k-console__nameline">
          <h2 className="k-console__name" title={field.name}>
            {field.name}
          </h2>
          <span className="k-mono k-caption k-console__area">
            {hectares(field.area_ha)} {T.tools.hectares}
          </span>
        </div>

        {(badgeLabel !== null || isSmall) && (
          <div className="k-console__marks">
            {badgeLabel !== null && (
              <span
                className="k-badge k-console__use"
                data-testid="field-use-badge"
                title={fieldUse?.field_use_ru ?? undefined}
              >
                {badgeLabel}
              </span>
            )}
            {isSmall && (
              <span className="k-console__small" data-testid="small-field">
                <span aria-hidden="true">ⓘ</span> {T.tools.smallFieldThesis(smallThreshold)}
                <details className="k-details">
                  <summary>{T.tools.smallFieldWhy}</summary>
                  <p className="k-caption">
                    {T.tools.smallField(smallThreshold, radarUnreliable ? radarThreshold : null)}
                  </p>
                </details>
              </span>
            )}
          </div>
        )}

        {tabsVisible && (
          <ConsoleTabs
            active={tab}
            onChange={setTab}
            /* Счёт — ЗАМЕТОК, и считается он по СПИСКУ ЗАМЕТОК. Висел на состоянии
               фотографий: цифра гасла на каждом перечите фото — то есть после всякой удачной
               загрузки, — и ярлык мигал там, где число не менялось вовсе. Нет его, пока не
               приехал свой список: «Заметки 0» утверждало бы отсутствие раньше, чем мы
               спросили. */
            count={notes.notesState?.phase === "idle" ? notes.notes.length : null}
          />
        )}
      </header>

      {/* ТЕЛО — единственный скролл колонки, и это ПАНЕЛЬ ВКЛАДКИ: смонтирована ровно одна.
          `tabIndex={0}` обязателен — без него прокручиваемую область нельзя листать с
          клавиатуры. Прокрутка не восстанавливается: каждая вкладка открывается сверху. */}
      {showNotes && notes !== null ? (
        <div
          className="k-console__body k-scroll"
          id={TAB_IDS.notes.panel}
          role="tabpanel"
          aria-labelledby={TAB_IDS.notes.tab}
          tabIndex={0}
        >
          {errorNote}
          <FieldNotes panel={notes} />
        </div>
      ) : (
        <div
          className="k-console__body k-scroll"
          id={tabsVisible ? TAB_IDS.analysis.panel : undefined}
          role={tabsVisible ? "tabpanel" : undefined}
          aria-labelledby={tabsVisible ? TAB_IDS.analysis.tab : undefined}
          tabIndex={tabsVisible ? 0 : undefined}
        >
          {analysisPanel}
        </div>
      )}

      {/* ПАНЕЛЬ ДЕЙСТВИЙ. Это действия ПОЛЯ, а не вкладки, поэтому она стоит на обеих; высота
          постоянна во всех состояниях и при переключении вкладок — кнопка анализа она же
          индикатор прогона, и текст внутри неё меняется, а размер полосы нет. */}
      <div className="k-console__actions" data-testid="console-actions">
        <button
          type="button"
          className={`k-btn ${runIsGoal ? "k-btn--go" : "k-btn--ghost"} k-console__run`}
          data-busy={busy || undefined}
          disabled={busy}
          onClick={() => onRun()}
        >
          {runLabelText}
        </button>
      </div>

      {devPanel}
    </aside>
  );
}
